// read-excel-file publishes no root export — only ./browser, ./node,
// ./universal and ./web-worker — so the subpath is required. ./browser is the
// one that keeps Node's fs out of the bundle.
import readXlsxFile from "read-excel-file/browser";
import moment from "moment";

/**
 * Parses a bulk payment .xlsx file into { headers, rows }.
 *
 * Uploads are Excel-only (see ACCEPTED_FILE_EXTENSIONS) — CSV is rejected at the
 * picker, so there is no CSV branch here. `headers` is the column order as it
 * appears in the sheet; `rows` is an array of plain objects keyed by those
 * headers, which is the shape validation, preview, and the upload payload all
 * expect.
 *
 * Only the first worksheet is read. read-excel-file defaults to sheet 1 and that
 * default is kept, so payments must live on the first tab.
 */

// An ISO date-time held in a *text* cell, e.g. "2026-07-07T18:30:00.000Z".
// Excel only types a cell as a date when it recognises the entry as one; values
// pasted from an API response stay text, so they need the same treatment.
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Normalizes a spreadsheet cell to a trimmed string.
 *
 * read-excel-file types cells natively, so a recognised date arrives as a Date
 * and numerics as numbers — but a date pasted as text stays a string, and the
 * API wants date-only "YYYY-MM-DD" either way. Both paths are converted through
 * moment's *local* parts rather than toISOString(): the backend stores IST
 * midnight as 18:30Z the previous day, so "2026-07-07T18:30:00.000Z" must go
 * back as 2026-07-08. Using the UTC date would send 2026-07-07 and shift every
 * such date a day earlier.
 */
const normalizeCell = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return moment(value).format("YYYY-MM-DD");

  const text = String(value).trim();
  if (ISO_DATE_TIME.test(text)) {
    const parsed = moment(text);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  }
  return text;
};

const isBlankRow = (row) =>
  !row || row.every((cell) => normalizeCell(cell) === "");

/** Carries a message intended for the user; the page shows `message` verbatim. */
export class PaymentFileError extends Error {}

/**
 * True when the file really is an OOXML workbook.
 *
 * .xlsx is a zip archive, so every valid one starts with the "PK" local file
 * header. A .csv or legacy .xls simply renamed to .xlsx passes the extension
 * check but fails to unzip, and read-excel-file's raw error for that
 * ("End of central directory not found") means nothing to a user — so the case
 * is caught here and named.
 */
const hasXlsxSignature = async (file) => {
  try {
    const head = new Uint8Array(await file.slice(0, 2).arrayBuffer());
    return head[0] === 0x50 && head[1] === 0x4b; // "PK"
  } catch {
    return false;
  }
};

/**
 * Normalizes read-excel-file's return value to [{ name, rows }].
 *
 * v9 resolves to one wrapper per worksheet — [{ sheet: "Name", data: [[…]] }] —
 * NOT the flat row matrix the docs' basic example implies. Treating that array
 * as rows makes every "row" an object, so cell-level work throws
 * "row.every is not a function". The flat shape is still accepted so a version
 * change in either direction can't silently break this.
 */
const toSheets = (parsed) => {
  if (!Array.isArray(parsed) || parsed.length === 0) return [];

  const wrapped =
    !Array.isArray(parsed[0]) && Array.isArray(parsed[0]?.data);

  if (wrapped) {
    return parsed.map((entry) => ({
      name: entry.sheet ?? null,
      rows: entry.data || [],
    }));
  }
  return [{ name: null, rows: parsed }];
};

export const parsePaymentFile = async (file) => {
  if (!(await hasXlsxSignature(file))) {
    throw new PaymentFileError(
      "This is not a real Excel workbook — it looks like a CSV or older .xls renamed to .xlsx. Open it in Excel and use Save As → Excel Workbook (.xlsx)."
    );
  }

  const sheets = toSheets(await readXlsxFile(file));

  // Workbooks often lead with a cover or blank tab, so the first sheet that has
  // any content wins rather than blindly taking sheet 1.
  const sheet = sheets.find((candidate) =>
    (candidate.rows || []).some((row) => !isBlankRow(row))
  );
  if (!sheet) {
    return { headers: [], rows: [], sheetName: null, sheetCount: sheets.length };
  }

  const matrix = sheet.rows;

  // Exported sheets often carry blank or title rows above the headers, so the
  // first row with any content is treated as the header row.
  const headerIndex = matrix.findIndex((row) => !isBlankRow(row));
  const headers = matrix[headerIndex].map((cell) => normalizeCell(cell));

  const rows = matrix
    .slice(headerIndex + 1)
    .filter((row) => !isBlankRow(row))
    .map((row) => {
      const record = {};
      headers.forEach((header, columnIndex) => {
        if (header) record[header] = normalizeCell(row[columnIndex]);
      });
      return record;
    });

  return {
    headers: headers.filter(Boolean),
    rows,
    sheetName: sheet.name,
    sheetCount: sheets.length,
  };
};

export default parsePaymentFile;
